/**
 * closeSelectionSync — hand the AI Workspace's shot back to the Timeline on the way out.
 *
 * The workspace keeps its OWN selection (useAiWorkspaceFlow.selectedShotIds), deliberately
 * decoupled from projectStore.selectedShotId since cs:3722. That decoupling is one-directional on
 * the way IN (the workspace seeds itself from the playhead, or from an explicit pre-select) and was
 * total on the way OUT: closing left the Timeline pointed at whatever it had been showing before,
 * so picking a shot in the grid, closing, and finding the old shot still highlighted was the
 * shipped behaviour.
 *
 * A module function rather than hook code on purpose: the caller is an unmount cleanup, so nothing
 * here may read React state, and it must be callable with a project that has already gone away.
 *
 * WHERE THIS IS WIRED MATTERS. It belongs to AiWorkspace.tsx's own unmount and nowhere else:
 *   • uiStore.closeAiWorkspace cannot see the flow's local selection (no store copy exists), runs
 *     for the board's Enhance dialog too, and is identity-pinned by useAiWorkspaceFlow.host.test.
 *   • the shared flow hook is used by BOTH hosts; the board dialog opens in place over a board that
 *     already owns its shot and must stay bit-for-bit unaffected.
 */
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
// The board-arrival latch only (read-and-clear). Import direction is safe: uiStore imports
// projectStore/tutorialStore, neither of which reaches back here — nothing imports this module
// but AiWorkspace.tsx — so this cannot close a cycle. Re-verify if that ever changes.
import { useUiStore } from '@/store/uiStore.ts';

/**
 * @param effectiveShotId The ACTIVE tab's effective shot at the moment of the close, or null.
 */
export function syncSelectionToTimelineOnClose(effectiveShotId: number | null): void {
  // Read-and-cleared FIRST, ahead of every early return: the latch belongs to exactly ONE
  // workspace close, so no path out of here may leave it armed. Bailing with it set would hand
  // the arm to the NEXT close — reachable without an intervening route write, since
  // enterAiWorkspaceFromBoard raw-sets workspaceRoute rather than going through
  // setWorkspaceRoute. What it means is spelled out at the write below.
  const boardArrivalArmed = useUiStore.getState().consumeBoardPanelAssert();
  if (effectiveShotId == null) return;

  const ps = useProjectStore.getState();
  const project = ps.project;
  // Closed project (or a close that raced a project teardown) — there is no timeline to sync to.
  if (!project) return;
  // The shot's own scene, found with the SAME `!isOmitted` filter the two playhead helpers apply
  // (projectDerived.allShots, which both getShotStartTime and getShotAtTime are built on). An
  // omitted scene's shots do not exist as far as those helpers are concerned — getShotStartTime
  // answers 0 for them, the same answer it gives for an id the project never had — so a shot that
  // turns up only inside one has to fall through to the same no-op as a missing shot, not sail
  // past this guard and seek the playhead to the top of the movie.
  const shotScene = project.scenes.find(
    scene => !scene.isOmitted && scene.shots.some(shot => shot.id === effectiveShotId),
  );
  // A shot the project no longer contains: a reload or a delete landed while the workspace was up.
  // Selecting it would leave a highlight pointing at nothing and seek the playhead to 0
  // (getShotStartTime's answer for an unknown id).
  if (!shotScene) return;

  // The Timeline is scoped to ONE scene (SceneSelector) and this shot lives in a different one.
  // Reachable by simply picking it: the workspace grid lists every scene and knows nothing about
  // the lane's scope. Both halves of the write would misfire — the scoped lane never renders the
  // shot, so the highlight lands on nothing visible, and seek() clamps into the active scene's
  // window (playbackStore.ts:47-56), parking the playhead on that scene's edge and leaving the
  // Viewer showing a shot the user did not choose. Skip BOTH, which is precisely what
  // ShotLane.tsx:236-248 already does when a board selection arrives from outside the scope.
  if (ps.activeSceneId != null && shotScene.id !== ps.activeSceneId) return;

  // Already there. No re-select (it would clear selectedAudioClipId) and no seek (it would move a
  // playhead the user never asked to move) — but an ARMED board arrival still owes the panel this
  // shot, exactly as setWorkspaceRoute's immediate assert does for an unchanged selection. The
  // tile AI chip pre-selects cold, so without this a round trip that picks nothing new lands back
  // on the cold tile (owner bug 20260902).
  if (ps.selectedShotId === effectiveShotId) {
    if (boardArrivalArmed && !ps.userInitiatedShotSelection) {
      useProjectStore.setState({ userInitiatedShotSelection: true });
    }
    return;
  }

  // userInitiated FALSE by default, matching every Timeline-side select (ShotBar.tsx). That flag
  // is the BOARD's shot-actions-panel gate, and arming it unconditionally here would make closing
  // the workspace pop a panel on the user's next visit to the board — which is why a close that
  // lands back on the TIMELINE (the X button or Esc on a workspace the Timeline opened) stays
  // false.
  //
  // It arms for an ARRIVAL at the Board, of which there are two: a click on the Board TAB while
  // the workspace is open (setWorkspaceRoute), and a returnToBoard close (closeAiWorkspace —
  // the X button on a workspace the board itself opened, 20260902). Either way the user is
  // landing on the board, so its selected state must come back whole, panel included.
  // setWorkspaceRoute asserts exactly that for the tab click — but it runs synchronously inside
  // the click, against the selection the TIMELINE had, and this write (a post-commit unmount
  // cleanup) lands afterwards and rewrites both fields in one set, so the assert alone loses the
  // race; the returnToBoard close never routes at all, so it has no assert of its own. uiStore
  // arms a one-shot latch on both arrivals for us to consume here.
  //
  // The consume is read-and-clear (see the top of this function), so a StrictMode double-cleanup
  // cannot pop a second panel. With it armed the board also expands and scrolls to the shot's
  // scene (SceneBoardExpanded's reveal effect) — intended on an explicit arrival.
  ps.selectShot(effectiveShotId, boardArrivalArmed);

  // Selection alone is not enough to make the shot visible. The big Viewer is playhead-driven, not
  // selection-driven (Viewer.tsx), and ShotLane's entry scroll/seek is a one-shot per MOUNT that
  // already fired — the lane never unmounts while the workspace is open. Without this the highlight
  // and the picture disagree. Pairing select with seek is exactly what a Timeline tap does
  // (ShotBar.tsx:116-122).
  const playback = usePlaybackStore.getState();
  // Not while running: the transport is disabled for the whole time the workspace is up, so this
  // should be unreachable — but if playback ever is live, moving the playhead under it is a scrub,
  // and "put the highlight here" must never turn into "jump the movie".
  if (playback.isPlaying) return;
  // Already showing it — nothing to move.
  if (ps.getShotAtTime(playback.currentTime)?.id === effectiveShotId) return;
  playback.seek(ps.getShotStartTime(effectiveShotId));
}
